/**
 * CoachMarkOverlay — the guided-tutorial spotlight. Renders an instruction
 * banner, a highlight ring that tracks a (possibly moving) screen-space target
 * every frame, and a bouncing arrow pointing at it. A dimmed backdrop focuses
 * attention on the ring.
 *
 * Crucially the whole overlay is `pointer-events: none` EXCEPT the Skip button,
 * so the player's taps pass straight through to the real dice/wallet/bet
 * subsystems underneath — the coach mark is pure visual guidance layered on top
 * of the live scene, never an input gate. Targets are provided as functions
 * returning a screen-space rect (3D objects project via the camera), so a step
 * can spotlight a die as it settles or the wallet as it slides in.
 *
 * Self-driven: an internal rAF loop re-evaluates the active step's target each
 * frame, so callers don't need to wire it into the engine loop.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { sound, SoundId } from '../../engine/audio/sound';

/** Screen-space rectangle in CSS pixels (origin top-left). */
export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CoachStep {
  /** Primary instruction line. */
  text: string;
  /** Optional secondary line (smaller, dimmer). */
  subtext?: string;
  /**
   * Highlight target, re-evaluated every frame. Return null for a banner-only
   * step (full-screen soft dim, no ring) — e.g. an intro beat.
   */
  target?: () => ScreenRect | null;
  /** Banner placement. Defaults to 'bottom'. */
  bannerPosition?: 'top' | 'bottom' | 'center';
  /**
   * Which side of the highlight ring the pointer arrow sits on. Defaults to
   * 'top' (arrow above the ring). Use 'bottom' when the target hugs the top of
   * the screen (e.g. the home stat row) so the arrow points up at it from below
   * instead of being shoved into the status bar.
   */
  arrowPosition?: 'top' | 'bottom';
  /**
   * Anchor the banner ~30px above a screen point (e.g. the local bet zone),
   * re-evaluated each frame. Overrides bannerPosition when it returns a point.
   */
  bannerAnchor?: () => { x: number; y: number } | null;
  /** Padding (px) added around the target rect for the ring. Default 12. */
  pad?: number;
}

interface CoachMarkEvents {
  /** Mid-tutorial Skip pressed — caller should grant the full reward + exit. */
  'skip': void;
}

const STYLE_ID = 'sdl-coach-styles';

export class CoachMarkOverlay extends EventEmitter<CoachMarkEvents> {
  public element: HTMLElement;
  private ringEl: HTMLElement;
  private dimEl: HTMLElement;
  private bannerEl: HTMLElement;
  private bannerBodyEl: HTMLElement;
  private bannerTextEl: HTMLElement;
  private bannerSubEl: HTMLElement;
  private successEl: HTMLElement;
  private arrowEl: HTMLElement;
  private skipEl: HTMLElement;

  private step: CoachStep | null = null;
  /** Last-written banner / ring geometry — skips redundant per-rAF style writes. */
  private lastBannerSig = '';
  private lastRingSig = '';
  private visible = false;
  private rafId: number | null = null;
  /** When false, only the instruction banner shows — no dim / ring / arrow. */
  private highlight: boolean;

  constructor(opts: { showSkip?: boolean; highlight?: boolean } = {}) {
    super();
    this.highlight = opts.highlight !== false;
    CoachMarkOverlay.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-coach-host';
    this.element.innerHTML = `
      <div class="sdl-coach-dim"></div>
      <div class="sdl-coach-ring"></div>
      <div class="sdl-coach-arrow">▲</div>
      <div class="sdl-coach-banner">
        <div class="sdl-coach-success" aria-hidden="true">
          <svg viewBox="0 0 52 52"><circle cx="26" cy="26" r="24"/><path d="M14 27 l8 8 l16 -18"/></svg>
        </div>
        <div class="sdl-coach-banner-body">
          <div class="sdl-coach-banner-text"></div>
          <div class="sdl-coach-banner-sub"></div>
        </div>
      </div>
      <button class="sdl-coach-skip" data-skip>Skip tutorial</button>
    `;
    document.body.appendChild(this.element);

    this.dimEl = this.element.querySelector('.sdl-coach-dim') as HTMLElement;
    this.ringEl = this.element.querySelector('.sdl-coach-ring') as HTMLElement;
    this.arrowEl = this.element.querySelector('.sdl-coach-arrow') as HTMLElement;
    this.bannerEl = this.element.querySelector('.sdl-coach-banner') as HTMLElement;
    this.bannerBodyEl = this.element.querySelector('.sdl-coach-banner-body') as HTMLElement;
    this.bannerTextEl = this.element.querySelector('.sdl-coach-banner-text') as HTMLElement;
    this.bannerSubEl = this.element.querySelector('.sdl-coach-banner-sub') as HTMLElement;
    this.successEl = this.element.querySelector('.sdl-coach-success') as HTMLElement;
    this.skipEl = this.element.querySelector('[data-skip]') as HTMLElement;

    if (!opts.showSkip) this.skipEl.style.display = 'none';
    this.skipEl.addEventListener('click', () => this.emit('skip', undefined));
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('visible');
    this.startLoop();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('visible');
    this.stopLoop();
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** Show a step (or pass null to clear ring + banner while staying mounted). */
  setStep(step: CoachStep | null): void {
    this.step = step;
    // New step → force the first frame's style writes through the caches.
    this.lastBannerSig = '';
    this.lastRingSig = '';
    if (!step) {
      this.bannerEl.classList.remove('visible');
      this.ringEl.classList.remove('visible');
      this.arrowEl.classList.remove('visible');
      this.dimEl.classList.remove('visible');
      return;
    }
    this.bannerTextEl.textContent = step.text;
    this.bannerSubEl.textContent = step.subtext ?? '';
    this.bannerSubEl.style.display = step.subtext ? 'block' : 'none';
    this.bannerEl.classList.toggle('top', step.bannerPosition === 'top');
    this.bannerEl.classList.toggle('center', step.bannerPosition === 'center');

    // Reset inline positioning when this step isn't zone-anchored, so the CSS
    // top/bottom placement applies.
    if (!step.bannerAnchor) {
      this.bannerEl.style.left = '';
      this.bannerEl.style.top = '';
      this.bannerEl.style.bottom = '';
      this.bannerEl.style.transform = '';
    }

    // Clear any prior step-complete state (dark-blue bg, success icon, label-out).
    this.bannerEl.classList.remove('complete');
    this.successEl.classList.remove('show');

    this.bannerEl.classList.add('visible');

    // Replay the label-in animation: opacity + scale 0.6→1, back-out ease.
    this.bannerBodyEl.classList.remove('label-out', 'label-in');
    void this.bannerBodyEl.offsetWidth; // reflow so the animation restarts
    this.bannerBodyEl.classList.add('label-in');

    // Ring/arrow/dim visibility is settled per-frame in update() based on the
    // current target rect (target may not resolve yet on the first frame).
    this.update();
  }

  /**
   * Play the step-complete beat (the departing state's onExit): pop a green
   * success icon, fade the banner background to dark blue, then animate the
   * instruction label out. Resolves when the beat is done so the caller can
   * advance state and call setStep() for the next instruction (which animates
   * the next label in).
   */
  playStepComplete(): Promise<void> {
    return new Promise((resolve) => {
      this.bannerEl.classList.add('complete'); // bg → dark blue
      this.successEl.classList.add('show');    // success icon pops in
      sound.play(SoundId.STEP_COMPLETE);       // win stinger rides the check pop-in
      window.setTimeout(() => this.bannerBodyEl.classList.add('label-out'), 520);
      window.setTimeout(() => resolve(), 950);
    });
  }

  showSkip(show: boolean): void {
    this.skipEl.style.display = show ? '' : 'none';
  }

  destroy(): void {
    this.stopLoop();
    this.element.remove();
    this.removeAllListeners();
  }

  private startLoop(): void {
    if (this.rafId != null) return;
    const tick = () => {
      this.update();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopLoop(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Re-position the ring + arrow from the active step's live target. */
  private update(): void {
    const step = this.step;
    if (!step) return;

    // Banner follow (works in label-only mode too): pin the banner so its
    // bottom edge sits ~30px above the anchored screen point.
    if (step.bannerAnchor) {
      const pt = step.bannerAnchor();
      if (pt) {
        // Skip the style writes when the anchor hasn't moved — this loop runs
        // every rAF while a coach mark is up, and most targets are static.
        const sig = `${pt.x}|${pt.y}`;
        if (sig !== this.lastBannerSig) {
          this.lastBannerSig = sig;
          this.bannerEl.style.left = `${pt.x}px`;
          this.bannerEl.style.top = `${pt.y - 30}px`;
          this.bannerEl.style.bottom = 'auto';
          this.bannerEl.style.transform = 'translate(-50%, -100%)';
        }
      }
    }

    // Label-only mode: keep dim / ring / arrow off, banner stays (set in setStep).
    if (!this.highlight) {
      this.dimEl.classList.remove('visible');
      this.ringEl.classList.remove('visible');
      this.arrowEl.classList.remove('visible');
      return;
    }

    const rect = step.target ? step.target() : null;
    if (!rect) {
      // Banner-only beat: soft full-screen dim, no ring/arrow.
      this.dimEl.classList.add('visible');
      this.ringEl.classList.remove('visible');
      this.arrowEl.classList.remove('visible');
      return;
    }

    const pad = step.pad ?? 12;
    const x = rect.x - pad;
    const y = rect.y - pad;
    const w = rect.width + pad * 2;
    const h = rect.height + pad * 2;

    // The ring's huge box-shadow spread IS the dim, so hide the flat dim layer.
    this.dimEl.classList.remove('visible');
    this.ringEl.classList.add('visible');
    this.arrowEl.classList.add('visible');

    // Only write ring/arrow styles when the target actually moved — a style
    // write here dirties a repaint under the ring's full-screen box-shadow
    // dim, and this runs every rAF on mostly static targets.
    const ringSig = `${x}|${y}|${w}|${h}|${step.arrowPosition ?? ''}`;
    if (ringSig === this.lastRingSig) return;
    this.lastRingSig = ringSig;

    this.ringEl.style.transform = `translate(${x}px, ${y}px)`;
    this.ringEl.style.width = `${w}px`;
    this.ringEl.style.height = `${h}px`;

    // Arrow bobs next to the ring, pointing at it. Default above; 'bottom'
    // tucks it under the ring (for targets that hug the top of the screen).
    const arrowX = x + w / 2;
    const arrowY = step.arrowPosition === 'bottom' ? y + h + 6 : y - 26;
    this.arrowEl.style.transform = `translate(${arrowX}px, ${arrowY}px) translateX(-50%)`;
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-coach-host {
        position: fixed; inset: 0;
        z-index: 760;
        display: none;
        pointer-events: none;
      }
      .sdl-coach-host.visible { display: block; }

      .sdl-coach-dim {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.55);
        opacity: 0; transition: opacity 0.25s;
      }
      .sdl-coach-dim.visible { opacity: 1; }

      .sdl-coach-ring {
        position: absolute; top: 0; left: 0;
        width: 0; height: 0;
        border: 2px solid #00E5FF;
        border-radius: 16px;
        box-shadow: 0 0 0 4000px rgba(0,0,0,0.6), 0 0 18px rgba(0,229,255,0.8);
        opacity: 0; transition: opacity 0.25s;
        will-change: transform, width, height;
      }
      .sdl-coach-ring.visible { opacity: 1; }

      .sdl-coach-arrow {
        position: absolute; top: 0; left: 0;
        color: #00E5FF;
        font-size: 22px;
        text-shadow: 0 0 8px rgba(0,229,255,0.9);
        opacity: 0;
        animation: sdl-coach-bob 0.9s ease-in-out infinite;
      }
      .sdl-coach-arrow.visible { opacity: 1; }
      @keyframes sdl-coach-bob {
        0%, 100% { margin-top: 0; }
        50% { margin-top: 6px; }
      }

      .sdl-coach-banner {
        position: absolute;
        left: 50%; bottom: 96px;
        transform: translateX(-50%);
        width: min(86vw, 360px);
        min-height: 30px;
        padding: 14px 18px;
        background: rgba(10,10,14,0.92);
        border: 1px solid rgba(0,229,255,0.35);
        border-radius: 16px;
        box-shadow: 0 12px 36px rgba(0,0,0,0.5);
        text-align: center;
        opacity: 0;
        transition: opacity 0.25s, background 0.4s ease, border-color 0.4s ease;
      }
      .sdl-coach-banner.visible { opacity: 1; }
      .sdl-coach-banner.top { top: 80px; bottom: auto; }
      .sdl-coach-banner.center {
        top: 34%; bottom: auto;
        transform: translate(-50%, -50%);
      }
      .sdl-coach-banner.complete {
        background: #0b1f4d;
        border-color: rgba(90,150,255,0.6);
      }
      .sdl-coach-banner-body { transition: opacity 0.3s ease, transform 0.3s ease; }
      .sdl-coach-banner-body.label-in {
        animation: sdl-coach-label-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      .sdl-coach-banner-body.label-out {
        opacity: 0; transform: translateY(-4px);
      }
      @keyframes sdl-coach-label-in {
        0% { opacity: 0; transform: scale(0.6); }
        100% { opacity: 1; transform: scale(1); }
      }
      .sdl-coach-banner-text {
        font-family: 'Rajdhani', sans-serif;
        font-size: 17px; font-weight: 600; line-height: 1.3;
        color: #fff;
      }
      .sdl-coach-banner-sub {
        font-family: 'Rajdhani', sans-serif;
        font-size: 14px; font-weight: 500; line-height: 1.3;
        /* 0.55 was borderline over the bright 3D scene — secondary text on
           dark stays ≥0.78; hierarchy vs the title comes from size/weight. */
        color: rgba(255,255,255,0.8);
        margin-top: 4px;
      }
      .sdl-coach-success {
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%, -50%) scale(0.5);
        opacity: 0; pointer-events: none;
      }
      .sdl-coach-success.show {
        animation: sdl-coach-success-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes sdl-coach-success-in {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.4) rotate(-12deg); }
        100% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
      }
      .sdl-coach-success svg { width: 46px; height: 46px; display: block; }
      .sdl-coach-success circle { fill: none; stroke: #3ad07a; stroke-width: 3; }
      .sdl-coach-success path {
        fill: none; stroke: #3ad07a; stroke-width: 4;
        stroke-linecap: round; stroke-linejoin: round;
      }

      .sdl-coach-skip {
        position: absolute;
        top: max(14px, env(safe-area-inset-top));
        right: max(14px, env(safe-area-inset-right));
        pointer-events: auto;
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.25);
        color: rgba(255,255,255,0.88);
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px; letter-spacing: 1px; font-weight: 600;
        padding: 6px 12px; border-radius: 10px;
        cursor: pointer;
      }
      .sdl-coach-skip:active { background: rgba(255,255,255,0.16); }
    `;
    document.head.appendChild(style);
  }
}
